// src/components/admin/settings/ContactInfoSettings.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaUpload, FaTrash } from 'react-icons/fa';
import './SettingsPages.css';

const ContactInfoSettings = () => {
  const [settings, setSettings] = useState({
    footer_logo: '',
    store_description: '',
    store_email: 'info@shopdemo.com',
    store_phone: '+1 (555) 123-4567',
    store_address: '123 Demo Street, City'
  });
  const [version, setVersion] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(
        `https://demo-site.storii.com.pk/demo-api/public/get-settings.php?t=${Date.now()}`,
        { cache: 'no-store' }
      );
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(prev => ({ ...prev, ...data.settings }));
        if (data.version) setVersion(data.version);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size should be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    setUploadingLogo(true);

    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/upload-footer-logo.php', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        toast.error(`Upload failed — server returned ${response.status}`);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSettings({ ...settings, footer_logo: data.image_url });
        setVersion(Date.now());
        toast.success('Footer logo uploaded!');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    setRemovingLogo(true);
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ footer_logo: '' })
      });

      if (!response.ok) {
        toast.error(`Failed to remove logo — server returned ${response.status} (endpoint may be missing)`);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSettings({ ...settings, footer_logo: '' });
        toast.success('Footer logo removed');
      } else {
        toast.error('Failed to remove logo');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setRemovingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        toast.error(`Save failed — server returned ${response.status} (endpoint may be missing)`);
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Contact info saved!');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loader">Loading contact info...</div>;

  return (
    <div className="settings-page">
      <h2>Footer Information</h2>
      <p className="section-description">Update your store's footer logo, description, and contact details</p>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-group">
          <label>Footer Logo</label>
          <div className="logo-upload-area">
            {settings.footer_logo && (
              <div className="logo-preview">
                <img src={`${settings.footer_logo}?v=${version}`} alt="Footer logo" />
                <button type="button" className="remove-logo-btn" onClick={handleRemoveLogo} disabled={removingLogo}>
                  <FaTrash /> {removingLogo ? 'Removing...' : 'Remove'}
                </button>
              </div>
            )}
            <input type="file" id="footerLogoUpload" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
            <button type="button" className="upload-btn" onClick={() => document.getElementById('footerLogoUpload').click()} disabled={uploadingLogo}>
              <FaUpload /> {uploadingLogo ? 'Uploading...' : 'Upload Footer Logo'}
            </button>
            <small className="field-hint">Max file size: 2MB. If left empty, the store name text is shown instead.</small>
          </div>
        </div>

        <div className="form-group">
          <label>Text Under Logo</label>
          <textarea
            name="store_description"
            value={settings.store_description}
            onChange={handleChange}
            rows="3"
            placeholder="A short line describing your store, shown under the logo in the footer"
          />
        </div>

        <div className="form-group">
          <label>Store Email</label>
          <input type="email" name="store_email" value={settings.store_email} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Store Phone</label>
          <input type="text" name="store_phone" value={settings.store_phone} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Store Address</label>
          <textarea name="store_address" value={settings.store_address} onChange={handleChange} rows="3" />
        </div>
        <button type="submit" disabled={saving} className="save-btn">{saving ? 'Saving...' : 'Save Contact Info'}</button>
      </form>
    </div>
  );
};

export default ContactInfoSettings;